import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Target, Heart, TrendingUp, ArrowRight } from 'lucide-react';
import { STATS, BRAND } from '../mock';
import { Button } from '../components/ui/button';
import useReveal from '../hooks/useReveal';

const VALUES = [
  { icon: Target, title: 'Reliability first', desc: 'We treat every delivery as mission-critical \u2014 because to our clients, it is.' },
  { icon: Heart, title: 'People-powered', desc: 'Professional, courteous drivers who represent your business well at every doorstep.' },
  { icon: TrendingUp, title: 'Always improving', desc: 'Smart dispatch technology and route optimization to keep getting faster.' },
];

export default function About() {
  useReveal();
  useEffect(() => { document.title = 'About StreetFleet | Toronto & GTA Courier Company'; }, []);

  return (
    <>
      <section className="bg-[#4E4E50] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-white">
          <span className="text-[#4FC3E0] font-bold uppercase tracking-wider text-sm">About Us</span>
          <h1 className="text-4xl sm:text-5xl font-extrabold mt-2 mb-4">Moving Toronto, one delivery at a time</h1>
          <p className="text-gray-200 text-lg max-w-2xl">StreetFleet is a Toronto-based courier company built on speed, reliability and genuine local knowledge of the GTA.</p>
        </div>
      </section>
      <div className="h-1 bg-gradient-to-r from-[#17A2C4] to-[#4FC3E0]" />

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div className="reveal">
            <img src="https://images.unsplash.com/photo-1645736315000-6f788915923b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxsb2dpc3RpY3MlMjB3YXJlaG91c2V8ZW58MHx8fHwxNzg5OTE3NTc1fDA&ixlib=rb-4.1.0&q=85" alt="StreetFleet logistics operations" className="rounded-2xl shadow-xl w-full h-[420px] object-cover" />
          </div>
          <div className="reveal">
            <h2 className="text-3xl font-extrabold text-[#4E4E50] mb-4">Our story</h2>
            <p className="text-gray-600 mb-4">For over 15 years, StreetFleet has been the courier partner Toronto businesses trust when a delivery absolutely has to arrive on time. What started as a small team of bike messengers has grown into a full fleet serving the entire Greater Toronto Area.</p>
            <p className="text-gray-600 mb-4">From same-day parcels to after-hours emergencies, we combine hard-earned street knowledge with modern dispatch technology so your shipment always takes the fastest route.</p>
            <p className="text-gray-600">Whether you&rsquo;re a law firm sending time-sensitive documents or a supplier moving critical parts, we&rsquo;re on the road for you.</p>
          </div>
        </div>
      </section>

      <section className="bg-[#17A2C4] py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.label} className="text-center text-white">
              <div className="text-4xl sm:text-5xl font-extrabold mb-1">{s.value}</div>
              <div className="text-white/80 text-sm font-semibold uppercase tracking-wide">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 bg-[#f5f7f8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-12 reveal">
            <span className="text-[#17A2C4] font-bold uppercase tracking-wider text-sm">Our Values</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#4E4E50] mt-2">What drives us</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={v.title} className="reveal bg-white rounded-xl p-7 shadow-sm border border-gray-100 text-center" data-delay={i * 100}>
                  <div className="w-14 h-14 rounded-xl bg-[#17A2C4]/10 flex items-center justify-center mx-auto mb-4"><Icon size={26} className="text-[#17A2C4]" /></div>
                  <h3 className="font-extrabold text-[#4E4E50] text-lg mb-2">{v.title}</h3>
                  <p className="text-gray-600 text-sm">{v.desc}</p>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-12 reveal">
            <Link to="/contact"><Button className="bg-[#17A2C4] hover:bg-[#128aa8] text-white font-bold px-8 py-6 gap-2">Work with us <ArrowRight size={18} /></Button></Link>
          </div>
        </div>
      </section>
    </>
  );
}
